import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Check, Upload, Crown, TrendingUp, Users,
  Zap, Award, ShieldCheck, Sparkles, Star,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { FORM_OPTIONS, HERO_IMAGES } from "../lib/mockData";

const STEPS = [
  { num: 1, label: "About the firm" },
  { num: 2, label: "Practice details" },
  { num: 3, label: "Credentials" },
  { num: 4, label: "Pricing & tier" },
  { num: 5, label: "Review" },
];

const BENEFITS = [
  { icon: TrendingUp, t: "4× more qualified leads", d: "Premium firms average 40+ high-intent innovator briefs per month." },
  { icon: Users, t: "PAN-India visibility", d: "Reach 12,000+ innovators, startups, and R&D teams — instantly." },
  { icon: Award, t: "Verified badge & trust", d: "The Udhyak Verified badge is India's fastest-growing IP trust signal." },
  { icon: Zap, t: "Zero acquisition cost", d: "Marketplace-driven leads — pay only for value delivered." },
];

const FIRM_TYPES = ["Independent Patent Agent", "Small IP Boutique (< 5 attorneys)", "Mid-sized firm (5–20 attorneys)", "Large firm (20+ attorneys)", "IP department of a law firm"];
const YEARS_IN_PRACTICE = ["1–3 years", "3–7 years", "7–15 years", "15+ years"];
const TIERS = [
  { id: "basic", name: "Basic Listing", price: "Free", per: "always", feat: ["Standard profile", "3 leads/month cap", "Marketplace visibility"], recommended: false },
  { id: "premium", name: "Premium Profile", price: "₹14,999", per: "/ month", feat: ["Top-of-search placement", "Unlimited high-intent leads", "Editorial spotlight", "Verified Gold badge", "Priority lead routing", "Account manager"], recommended: true },
];

export default function AttorneyJoin() {
  const [step, setStep] = useState(1);
  const [firmType, setFirmType] = useState(FIRM_TYPES[1]);
  const [years, setYears] = useState(YEARS_IN_PRACTICE[2]);
  const [cities, setCities] = useState(["Delhi NCR", "Mumbai"]);
  const [langs, setLangs] = useState(["English", "Hindi"]);
  const [specs, setSpecs] = useState(["Software", "Biotech"]);
  const [tier, setTier] = useState("premium");

  const toggle = (val, list, setList) => setList(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);
  const next = () => setStep(Math.min(STEPS.length, step + 1));
  const back = () => setStep(Math.max(1, step - 1));

  return (
    <div className="surface-cream min-h-screen" data-testid="attorney-join-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden surface-deep text-white py-16 lg:py-20">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] blob bg-[#7CC242]" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] blob bg-[#0E6E5E]" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <div className="chip chip-gold inline-flex mb-5"><Crown size={13}/> For Patent Attorneys & IP Firms</div>
            <h1 className="font-display-tight text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-5">
              Grow your IP practice on <br/>
              <span className="text-[#7CC242]">India's largest</span> patent marketplace.
            </h1>
            <p className="text-lg text-white/75 max-w-xl leading-relaxed mb-8">
              12,000+ innovators, 850+ firms, and one shared mission — protect ideas. Join Udhyak, get matched with
              high-intent innovators, and grow your book of business without lifting a finger on acquisition.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#onboarding" className="btn-accent">Start Onboarding <ArrowRight size={15}/></a>
              <a href="#benefits" className="btn-outline bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0A2520]">See benefits</a>
            </div>

            <div className="grid grid-cols-3 gap-6 mt-10 pt-8 border-t border-white/10 max-w-md">
              <Stat label="Verified firms" value="850+"/>
              <Stat label="Avg. lead value" value="₹18K"/>
              <Stat label="Onboarding time" value="< 24h"/>
            </div>
          </div>

          <div className="relative">
            <img src={HERO_IMAGES.handshake} alt="" className="rounded-3xl aspect-[4/5] object-cover w-full"/>
            <div className="absolute -bottom-6 -left-6 bg-[#7CC242] text-[#0A2520] p-5 rounded-2xl max-w-[240px]">
              <Sparkles size={18} className="mb-2"/>
              <div className="font-display font-bold text-lg leading-tight mb-1">Free to join.</div>
              <div className="text-sm">Upgrade to Premium anytime — no lock-in.</div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="chip inline-flex mb-3">Why join Udhyak</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] leading-tight">Built for firms that want to grow.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b) => (
            <div key={b.t} className="card p-6">
              <div className="w-12 h-12 rounded-xl surface-sage flex items-center justify-center text-[#0E6E5E] mb-4">
                <b.icon size={20}/>
              </div>
              <div className="font-display font-bold text-lg text-[#0A2520] mb-2">{b.t}</div>
              <p className="text-[15px] text-[#5A6B66] leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ONBOARDING FORM */}
      <section id="onboarding" className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-[280px_1fr] gap-10 scroll-mt-20">
        <aside className="hidden lg:block">
          <div className="chip mb-4">Onboarding</div>
          <h2 className="font-display font-bold text-2xl text-[#0A2520] mb-1 leading-tight">Build your firm's profile.</h2>
          <p className="text-sm text-[#5A6B66] mb-8">Takes ~5 minutes · saved automatically</p>

          <div className="space-y-1">
            {STEPS.map((s) => {
              const active = s.num === step;
              const done = s.num < step;
              return (
                <button key={s.num} onClick={() => setStep(s.num)}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition ${active ? "surface-sage" : "hover:bg-[#F0F4F0]"}`}
                  data-testid={`atj-step-${s.num}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${done || active ? "bg-[#0E6E5E] text-white" : "bg-[#F0F4F0] text-[#5A6B66]"}`}>
                    {done ? <Check size={14} /> : s.num}
                  </div>
                  <div className={`font-display font-semibold ${active ? "text-[#0E6E5E]" : "text-[#0A2520]"}`}>{s.label}</div>
                </button>
              );
            })}
          </div>

          <div className="mt-10 card-flat p-4">
            <div className="flex gap-2 mb-2">
              <ShieldCheck size={16} className="text-[#0E6E5E] flex-shrink-0"/>
              <div className="font-display text-sm font-semibold text-[#0A2520]">Your data is safe</div>
            </div>
            <p className="text-sm text-[#5A6B66] leading-relaxed">Credentials are verified privately. Your profile goes live only after you approve it.</p>
          </div>
        </aside>

        <section className="card-flat p-7 lg:p-10" data-testid="attorney-onboarding-form">
          <div className="lg:hidden mb-6">
            <div className="flex items-center justify-between mb-2">
              {STEPS.map((s) => <div key={s.num} className={`flex-1 h-1 mx-0.5 rounded-full ${s.num <= step ? "bg-[#0E6E5E]" : "bg-[#E1E8E2]"}`}/>)}
            </div>
            <div className="text-sm text-[#5A6B66]">Step {step} of {STEPS.length}: {STEPS[step - 1].label}</div>
          </div>

          {step === 1 && (
            <>
              <div className="chip mb-3">Step 01</div>
              <h2 className="font-display font-bold text-3xl text-[#0A2520] mb-2">About your firm</h2>
              <p className="text-[#5A6B66] mb-8">A few basics so we can start building your profile.</p>

              <div className="grid sm:grid-cols-2 gap-5 mb-6">
                <F label="Firm / Firm-name">
                  <input className="input" placeholder="e.g. Lex India IP Associates" data-testid="atj-firm-name"/>
                </F>
                <F label="Website (optional)">
                  <input className="input" placeholder="https://" data-testid="atj-website"/>
                </F>
              </div>

              <F label="Type of firm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {FIRM_TYPES.map((t) => (
                    <button key={t} onClick={() => setFirmType(t)}
                      className={`text-left p-3.5 rounded-xl border font-semibold transition ${firmType === t ? "border-[#0E6E5E] surface-sage text-[#0B5447]" : "border-[#E1E8E2] bg-white text-[#3D4F4A] hover:border-[#7CC242]"}`}
                      data-testid={`atj-firm-type-${t}`}>{t}</button>
                  ))}
                </div>
              </F>

              <div className="grid sm:grid-cols-2 gap-5 mb-6">
                <F label="Primary contact person">
                  <input className="input" placeholder="Managing partner name" data-testid="atj-contact-name"/>
                </F>
                <F label="Designation">
                  <input className="input" placeholder="Managing Partner" data-testid="atj-designation"/>
                </F>
                <F label="Work email">
                  <input className="input" type="email" placeholder="partner@firm.in" data-testid="atj-email"/>
                </F>
                <F label="Phone">
                  <input className="input" type="tel" placeholder="+91 99999 88888" data-testid="atj-phone"/>
                </F>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="chip mb-3">Step 02</div>
              <h2 className="font-display font-bold text-3xl text-[#0A2520] mb-2">Practice details</h2>
              <p className="text-[#5A6B66] mb-8">Help innovators find you by domain, location, and language.</p>

              <F label="Years in IP practice">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {YEARS_IN_PRACTICE.map((t) => (
                    <button key={t} onClick={() => setYears(t)}
                      className={`text-center p-3.5 rounded-xl border font-semibold transition ${years === t ? "border-[#0E6E5E] surface-sage text-[#0B5447]" : "border-[#E1E8E2] bg-white text-[#3D4F4A] hover:border-[#7CC242]"}`}
                      data-testid={`atj-years-${t}`}>{t}</button>
                  ))}
                </div>
              </F>

              <F label="Cities you operate from">
                <div className="flex flex-wrap gap-2">
                  {FORM_OPTIONS.cities.map((c) => (
                    <button key={c} onClick={() => toggle(c, cities, setCities)}
                      className={`px-4 py-2 rounded-full font-semibold transition ${cities.includes(c) ? "bg-[#0E6E5E] text-white" : "bg-[#EBF3EC] text-[#0B5447] hover:bg-[#D5E2D6]"}`}
                      data-testid={`atj-city-${c}`}>{c}</button>
                  ))}
                </div>
              </F>

              <F label="Languages spoken">
                <div className="flex flex-wrap gap-2">
                  {FORM_OPTIONS.languages.map((l) => (
                    <button key={l} onClick={() => toggle(l, langs, setLangs)}
                      className={`px-4 py-2 rounded-full font-semibold transition ${langs.includes(l) ? "bg-[#0E6E5E] text-white" : "bg-[#EBF3EC] text-[#0B5447] hover:bg-[#D5E2D6]"}`}
                      data-testid={`atj-lang-${l}`}>{l}</button>
                  ))}
                </div>
              </F>

              <F label="Specialisations (pick up to 5)">
                <div className="flex flex-wrap gap-2">
                  {["Software","AI/ML","Semiconductor","SaaS","Biotech","Pharma","Chemistry","Mechanical","Electronics","Consumer Products","Agritech","Materials","Defence","Aerospace","Design","Trademark","Copyright","PCT"].map((s) => (
                    <button key={s} onClick={() => toggle(s, specs, setSpecs)}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition ${specs.includes(s) ? "bg-[#0E6E5E] text-white" : "bg-[#EBF3EC] text-[#0B5447] hover:bg-[#D5E2D6]"}`}
                      data-testid={`atj-spec-${s}`}>{s}</button>
                  ))}
                </div>
              </F>

              <F label="Short firm bio (150–400 chars)">
                <textarea rows={4} className="input resize-none leading-relaxed" placeholder="Tell innovators what makes your firm distinctive." data-testid="atj-bio"/>
              </F>
            </>
          )}

          {step === 3 && (
            <>
              <div className="chip mb-3">Step 03</div>
              <h2 className="font-display font-bold text-3xl text-[#0A2520] mb-2">Credentials & verification</h2>
              <p className="text-[#5A6B66] mb-8">Required for the Udhyak Verified badge. We keep everything confidential.</p>

              <div className="grid sm:grid-cols-2 gap-5 mb-6">
                <F label="Patent Agent Registration No.">
                  <input className="input" placeholder="INPA-XXXXX" data-testid="atj-inpa"/>
                </F>
                <F label="Bar Council enrollment no.">
                  <input className="input" placeholder="D/1234/2010" data-testid="atj-bar"/>
                </F>
              </div>

              <F label="Approximate patents filed to date">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {["< 25", "25–100", "100–500", "500+"].map((t, i) => (
                    <button key={t} className={`p-3.5 rounded-xl border font-semibold ${i === 2 ? "border-[#0E6E5E] surface-sage text-[#0B5447]" : "border-[#E1E8E2] bg-white text-[#3D4F4A] hover:border-[#7CC242]"}`}
                      data-testid={`atj-filed-${t}`}>{t}</button>
                  ))}
                </div>
              </F>

              <F label="Upload proof of registration">
                <div className="border-2 border-dashed border-[#DDE5DD] rounded-2xl p-8 text-center surface-sage hover:border-[#0E6E5E] transition cursor-pointer" data-testid="atj-upload-doc">
                  <Upload size={28} className="mx-auto text-[#0E6E5E] mb-3"/>
                  <div className="font-display font-semibold text-[#0A2520] mb-1">Upload Patent Agent Certificate</div>
                  <div className="text-sm text-[#5A6B66]">PDF, PNG, JPG · up to 10MB</div>
                </div>
              </F>

              <F label="Additional accreditations (optional)">
                <textarea rows={3} className="input resize-none" placeholder="e.g. AIPPI India Group member, WIPO Mediation certified, USPTO Registered Patent Attorney, ISO 27001 certified…" data-testid="atj-accreditations"/>
              </F>
            </>
          )}

          {step === 4 && (
            <>
              <div className="chip mb-3">Step 04</div>
              <h2 className="font-display font-bold text-3xl text-[#0A2520] mb-2">Pricing & listing tier</h2>
              <p className="text-[#5A6B66] mb-8">Set your starting rates and pick your listing tier. You can change these anytime.</p>

              <div className="grid sm:grid-cols-2 gap-5 mb-8">
                <F label="Starting price (utility patent)">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5A6B66] font-semibold">₹</span>
                    <input className="input pl-9" placeholder="16,500" data-testid="atj-price"/>
                  </div>
                </F>
                <F label="Median turnaround time">
                  <select className="input" data-testid="atj-tat">
                    <option>7–10 days</option><option>10–14 days</option><option>14–21 days</option><option>21–30 days</option>
                  </select>
                </F>
              </div>

              <F label="Pick a listing tier">
                <div className="grid md:grid-cols-2 gap-4">
                  {TIERS.map((t) => (
                    <button key={t.id} onClick={() => setTier(t.id)}
                      className={`text-left p-6 rounded-2xl border-2 transition ${tier === t.id ? "border-[#0E6E5E] surface-sage" : "border-[#E1E8E2] bg-white hover:border-[#7CC242]"}`}
                      data-testid={`atj-tier-${t.id}`}>
                      {t.recommended && <div className="chip chip-gold mb-3 inline-flex"><Crown size={11}/> Recommended</div>}
                      <div className="font-display font-bold text-xl text-[#0A2520] mb-1">{t.name}</div>
                      <div className="mb-4">
                        <span className="font-display font-extrabold text-3xl text-[#0A2520]">{t.price}</span>
                        <span className="text-sm text-[#5A6B66] ml-2">{t.per}</span>
                      </div>
                      <ul className="space-y-2">
                        {t.feat.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-[#3D4F4A]">
                            <Check size={14} className="text-[#0E6E5E] flex-shrink-0 mt-1"/> {f}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>
              </F>
            </>
          )}

          {step === 5 && (
            <>
              <div className="chip mb-3">Step 05</div>
              <h2 className="font-display font-bold text-3xl text-[#0A2520] mb-2">Review & submit</h2>
              <p className="text-[#5A6B66] mb-8">Everything looks good? We'll verify your credentials and get you live within 24 hours.</p>

              <div className="space-y-4 mb-8">
                <ReviewCard title="Firm type" value={firmType}/>
                <ReviewCard title="Years in practice" value={years}/>
                <ReviewCard title="Cities" value={cities.join(" · ")}/>
                <ReviewCard title="Languages" value={langs.join(" · ")}/>
                <ReviewCard title="Specialisations" value={specs.join(" · ")}/>
                <ReviewCard title="Listing tier" value={TIERS.find((t) => t.id === tier)?.name}/>
              </div>

              <label className="flex items-start gap-3 mb-6 text-sm text-[#5A6B66] cursor-pointer">
                <input type="checkbox" defaultChecked className="mt-1 accent-[#0E6E5E]"/>
                <span>I certify that all information provided is accurate and I agree to Udhyak's Attorney Terms &amp; Code of Conduct.</span>
              </label>

              <div className="surface-sage rounded-2xl p-5 mb-2">
                <div className="flex items-start gap-3">
                  <Star size={18} className="fill-[#7CC242] text-[#7CC242] flex-shrink-0 mt-0.5"/>
                  <div>
                    <div className="font-display font-semibold text-[#0A2520] mb-1">What happens after you submit?</div>
                    <p className="text-sm text-[#3D4F4A] leading-relaxed">Our onboarding team reviews credentials (usually within 24 hours), sets up your profile with your branding, and does a 30-min success call to optimise for lead conversion.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex items-center justify-between pt-7 border-t border-[#E1E8E2] mt-8">
            <button onClick={back} disabled={step === 1} className="btn-outline disabled:opacity-40 disabled:cursor-not-allowed" data-testid="atj-back">
              <ArrowLeft size={15}/> Back
            </button>
            {step < STEPS.length ? (
              <button onClick={next} className="btn-primary" data-testid="atj-next">
                Continue <ArrowRight size={15}/>
              </button>
            ) : (
              <button className="btn-primary" data-testid="atj-submit">
                <Check size={15}/> Submit for review
              </button>
            )}
          </div>
        </section>
      </section>

      <Footer/>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display font-extrabold text-2xl text-[#7CC242] mb-1">{value}</div>
      <div className="text-xs text-white/60 uppercase tracking-widest">{label}</div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="mb-7">
      <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2.5">{label}</label>
      {children}
    </div>
  );
}

function ReviewCard({ title, value }) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-xl surface-sage">
      <div className="text-sm text-[#5A6B66] font-semibold">{title}</div>
      <div className="text-right text-[#0A2520] font-semibold">{value}</div>
    </div>
  );
}
